// Lightweight share helper. Uses the native Web Share sheet when available
// (mobile + Capacitor WebView), and falls back to copying the link to the
// clipboard so "Share" always does something useful on desktop.

import { toast } from './toast';

export interface ShareInput {
  title?: string;
  text?: string;
  url: string;
}

export type ShareResult = 'shared' | 'copied' | 'dismissed' | 'failed';

export async function shareLink({ title, text, url }: ShareInput): Promise<ShareResult> {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url });
      return 'shared';
    } catch (err) {
      // User dismissed the sheet — distinct from a real failure, so callers can
      // stay silent instead of falling back to a confusing "copied" toast.
      if (err instanceof DOMException && err.name === 'AbortError') return 'dismissed';
      // Otherwise fall through to clipboard.
    }
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      // Copy the full text + link so the invite carries context (e.g. a join code).
      await navigator.clipboard.writeText([text, url].filter(Boolean).join(' ').trim() || url);
      return 'copied';
    }
  } catch {
    // ignore
  }
  return 'failed';
}

/**
 * Share with guaranteed user feedback: the native sheet when available, otherwise
 * copy to clipboard and confirm with a toast — so an "Invite"/"Share" tap never
 * silently no-ops. Dismissing the native sheet stays quiet.
 */
export async function shareWithFeedback(
  input: ShareInput,
  copiedMessage = 'Link copied to clipboard'
): Promise<ShareResult> {
  const result = await shareLink(input);
  if (result === 'copied') toast(copiedMessage);
  else if (result === 'failed') toast('Couldn’t open share — try again');
  return result;
}
